import * as React from "react";
import { IClient } from "intake_shared/src/types";
import { cn } from "../../../helpers";
import styles from "./ClientInquiryPreferences.module.scss";

export interface ClientInquiryPreferencesProps {
    client: IClient;
    className?: string;
    title?: string;
    /** Narrow column (e.g. Find Clinician sidebar): one column, tighter spacing. */
    variant?: "default" | "sidebar";
}

function humanizePart(raw: string): string {
    const t = String(raw).trim();
    if (!t) {
        return t;
    }
    if (t.includes("-") || t.includes("_")) {
        return t
            .split(/[-_]+/g)
            .filter(Boolean)
            .map((word) => {
                if (/^\d+$/.test(word)) {
                    return word;
                }
                if (word.length <= 2 && word === word.toUpperCase()) {
                    return word;
                }
                return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            })
            .join(" ");
    }
    if (t === t.toLowerCase() || t === t.toUpperCase()) {
        return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
    }
    return t;
}

function formatDisplayString(raw: string): string {
    if (raw.includes(",") && !raw.includes("\n")) {
        return raw
            .split(",")
            .map((p) => humanizePart(p))
            .filter(Boolean)
            .join(" · ");
    }
    return humanizePart(raw);
}

/** One formatted string per tag; splits lists into multiple tags. */
function formatValueParts(value: unknown): string[] {
    if (value === null || value === undefined) {
        return [];
    }
    if (Array.isArray(value)) {
        return value
            .map((v) => String(v).trim())
            .filter((s) => s.length > 0)
            .map((s) => formatDisplayString(s))
            .filter((s) => s.trim().length > 0);
    }
    if (typeof value === "object") {
        return [];
    }
    const s = String(value).trim();
    if (!s) {
        return [];
    }
    if (s.includes(",") && !s.includes("\n")) {
        return s
            .split(",")
            .map((p) => p.trim())
            .filter(Boolean)
            .map((p) => formatDisplayString(p))
            .filter((t) => t.trim().length > 0);
    }
    const one = formatDisplayString(s);
    return one.trim().length > 0 ? [one] : [];
}

type Row = { key: string; shortLabel: string; tags: string[] };

function buildRows(client: IClient): Row[] {
    const rows: Row[] = [];

    const push = (key: string, shortLabel: string, v: unknown) => {
        const tags = formatValueParts(v);
        if (tags.length > 0) {
            rows.push({ key, shortLabel, tags });
        }
    };

    push("therapist", "Therapists", client.requested_therapist);
    push("service", "Service", client.requested_service);
    push("location", "Location", client.requested_location);
    push("days", "Days", client.requested_day);
    push("time", "Time", client.requested_time);
    push("treatment", "Treatment", client.treatment_type);
    push("gender", "Gender", client.preferred_therapist_gender);
    push("concerns", "Concerns", client.presenting_concerns);
    push("language", "Language", client.preferred_language_of_communication);

    return rows;
}

/** True if the client has at least one non-empty matching preference to display. */
export function hasInquiryPreferences(client: IClient): boolean {
    return buildRows(client).length > 0;
}

export const ClientInquiryPreferences: React.FC<ClientInquiryPreferencesProps> = ({
    client,
    className,
    title = "Clinician matching preferences",
    variant = "default",
}) => {
    const rows = React.useMemo(() => buildRows(client), [client]);
    const isSidebar = variant === "sidebar";

    if (rows.length === 0) {
        return null;
    }

    return (
        <section
            className={cn(
                styles.wrapper,
                isSidebar && styles.wrapperSidebar,
                className
            )}
            aria-label={title}
        >
            <h2 className={cn(styles.title, isSidebar && styles.titleSidebar)}>
                {title}
            </h2>
            <ul className={cn(styles.bar, isSidebar && styles.barSidebar)}>
                {rows.map((row) => (
                    <li key={row.key} className={styles.group}>
                        <span className={styles.groupLabel}>{row.shortLabel}</span>
                        <div className={styles.tags} aria-label={row.shortLabel}>
                            {row.tags.map((t, i) => (
                                <span
                                    key={`${row.key}-tag-${i}`}
                                    className={styles.tag}
                                >
                                    {t}
                                </span>
                            ))}
                        </div>
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default ClientInquiryPreferences;
